import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Col, Container, Form, Row } from 'react-bootstrap';

import AppLayout from '../../../layouts/AppLayout/AppLayout';
import { useLanguage } from '../../../context/LanguageProvider/LanguageProvider';
import { useSiteSettings } from '../../../context/SiteSettingsProvider/SiteSettingsProvider';
import { getApiBaseUrl } from '../../../utils/apiBaseUrl';
import { NioSection, NioCard, NioField, NioButton, NioIcon, NioToaster } from '../../../components';

function ContactInfoBlock({ settings }) {
  const { t } = useLanguage();
  const email = settings.contactEmail || 'contact@jassp.com';
  const phone = settings.contactPhone || '+33 6 74 42 29 57';
  const phoneDigits = phone.replace(/\D/g, '');
  const addr1 = settings.contactAddressLine1 || '2B Rue Alfred Nobel';
  const addr2 = settings.contactAddressLine2 || '77420 Champs-sur-Marne, France';
  const company = settings.companyName || 'JASSP SAS';
  const hours = settings.businessHours || t('contact.info.hoursValue');
  return (
    <div className="d-flex flex-column gap-4">
      <NioCard className="border border-gray-200 shadow-sm rounded-3 h-100">
        <NioCard.Body className="p-4 p-lg-5">
          <h3 className="h5 mb-4 text-dark">{company}</h3>
          <ul className="list-unstyled fs-16 text-gray-700 mb-0">
            <li className="mb-4">
              <span className="fw-semibold text-dark d-block mb-1">{t('contact.info.address')}</span>
              {addr1}
              <br />
              {addr2}
            </li>
            <li className="mb-4">
              <span className="fw-semibold text-dark d-block mb-1">{t('contact.info.email')}</span>
              <a href={`mailto:${email}`} className="link-primary">
                {email}
              </a>
            </li>
            <li className="mb-4">
              <span className="fw-semibold text-dark d-block mb-1">{t('contact.info.phone')}</span>
              <a href={`tel:${phoneDigits}`} className="text-gray-700 text-decoration-none">
                {phone}
              </a>{' '}
              <span className="text-muted small">{t('contact.info.phoneFrance')}</span>
            </li>
            <li>
              <span className="fw-semibold text-dark d-block mb-1">{t('contact.info.hours')}</span>
              {hours}
            </li>
          </ul>
        </NioCard.Body>
      </NioCard>

      <NioCard className="border border-gray-200 shadow-sm rounded-3">
        <NioCard.Body className="p-4">
          <h4 className="h6 text-dark mb-3">{t('contact.otherWays.h4')}</h4>
          <div className="d-flex flex-wrap gap-2">
            {settings.socialLinkedin && (
              <a
                href={settings.socialLinkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline-secondary btn-sm"
              >
                <NioIcon name="linkedin-round" className="me-1" />
                LinkedIn
              </a>
            )}
            {settings.socialFacebook && (
              <a
                href={settings.socialFacebook}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline-secondary btn-sm"
              >
                <NioIcon name="facebook-f" className="me-1" />
                Facebook
              </a>
            )}
            {settings.socialWhatsapp && (
              <a
                href={`https://wa.me/${settings.socialWhatsapp.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline-success btn-sm"
              >
                WhatsApp
              </a>
            )}
          </div>
          <p className="small text-muted mb-0 mt-3">{t('contact.otherWays.note')}</p>
        </NioCard.Body>
      </NioCard>
    </div>
  );
}

function ContactPage() {
  const { t } = useLanguage();
  const { settings } = useSiteSettings();
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState(false);
  const [sendErrorMsg, setSendErrorMsg] = useState('');
  const [sending, setSending] = useState(false);

  const projectTypes = useMemo(
    () => [
      { value: '', label: t('contact.opt.projectType.empty') },
      { value: 'web-saas', label: t('contact.opt.projectType.webSaaS') },
      { value: 'observatory', label: t('contact.opt.projectType.observatory') },
      { value: 'blockchain', label: t('contact.opt.projectType.blockchain') },
      { value: 'mobile', label: t('contact.opt.projectType.mobile') },
      { value: 'research', label: t('contact.opt.projectType.research') },
      { value: 'other', label: t('contact.opt.projectType.other') },
    ],
    [t],
  );

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      fullName: '',
      email: '',
      phone: '',
      projectType: '',
      message: '',
    },
  });

  const onSubmit = async (data) => {
    setSendError(false);
    setSendErrorMsg('');
    setSending(true);
    try {
      const base = getApiBaseUrl();
      const res = await fetch(base ? `${base}/api/contact` : '/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        const key =
          payload.error === 'smtp_auth_failed'
            ? 'contact.toast.smtpAuthFailed'
            : payload.error === 'smtp_not_configured'
              ? 'contact.toast.smtpNotConfigured'
              : 'contact.toast.errorBody';
        setSendErrorMsg(t(key));
        setSendError(true);
        setTimeout(() => {
          setSendError(false);
          setSendErrorMsg('');
        }, 7000);
        return;
      }

      setSent(true);
      reset();
      setTimeout(() => setSent(false), 5000);
    } catch {
      setSendErrorMsg(t('contact.toast.errorBody'));
      setSendError(true);
      setTimeout(() => {
        setSendError(false);
        setSendErrorMsg('');
      }, 7000);
    } finally {
      setSending(false);
    }
  };

  return (
    <AppLayout variant={13} titleKey="pages.contact.title" rootClass="layout-12">
      {sent && (
        <NioToaster
          variant="success"
          messages={[
            {
              id: 1,
              header: t('contact.toast.sentTitle'),
              body: t('contact.toast.sentBody'),
            },
          ]}
        />
      )}
      {sendError && (
        <NioToaster
          variant="error"
          messages={[
            {
              id: 2,
              header: t('contact.toast.errorTitle'),
              body: sendErrorMsg || t('contact.toast.errorBody'),
            },
          ]}
        />
      )}

      <section className="jassp-contact-hero pt-7 pt-lg-120 pb-5 pb-lg-6">
        <Container>
          <Row className="justify-content-center text-center">
            <Col lg={10} xl={8}>
              <span className="jassp-contact-hero-kicker d-inline-block fs-14 text-uppercase fw-semibold mb-2">
                {t('contact.hero.kicker')}
              </span>
              <h1 className="jassp-contact-hero-h1 display-6 mb-3">{t('contact.hero.h1')}</h1>
              <p className="jassp-contact-hero-lead fs-18 mb-0">{t('contact.hero.lead')}</p>
            </Col>
          </Row>
        </Container>
      </section>

      <NioSection className="jassp-hero-light-gap pt-5 pb-5 pb-lg-7 bg-light">
        <Container>
          <Row className="g-4 g-lg-5 align-items-start">
            <Col lg={7}>
              <NioCard className="border border-gray-200 shadow-sm rounded-3">
                <NioCard.Body className="p-4 p-lg-5">
                  <h2 className="h4 mb-4 text-dark">{t('contact.form.h2')}</h2>
                  <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    <Row className="gy-3">
                      <Col xs={12}>
                        <NioField label={t('contact.form.fullName')} htmlFor="fullName">
                          <NioField.Input
                            id="fullName"
                            placeholder={t('contact.form.fullNamePh')}
                            errors={errors.fullName}
                            {...register('fullName', { required: t('contact.validation.required') })}
                          />
                        </NioField>
                      </Col>
                      <Col xs={12}>
                        <NioField label={t('contact.form.email')} htmlFor="email">
                          <NioField.Input
                            type="email"
                            id="email"
                            placeholder={t('contact.form.emailPh')}
                            errors={errors.email}
                            {...register('email', {
                              required: t('contact.validation.required'),
                              pattern: {
                                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                                message: t('contact.validation.invalidEmail'),
                              },
                            })}
                          />
                        </NioField>
                      </Col>
                      <Col xs={12}>
                        <NioField label={t('contact.form.phone')} htmlFor="phone">
                          <NioField.Input
                            id="phone"
                            type="tel"
                            placeholder={t('contact.form.phonePh')}
                            {...register('phone')}
                          />
                        </NioField>
                      </Col>
                      <Col xs={12}>
                        <Form.Group>
                          <Form.Label className="form-label">{t('contact.form.projectType')}</Form.Label>
                          <Form.Select
                            id="projectType"
                            className="form-control"
                            {...register('projectType', { required: t('contact.validation.projectType') })}
                          >
                            {projectTypes.map((opt) => (
                              <option key={opt.value || 'empty'} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </Form.Select>
                          {errors.projectType && (
                            <span className="nk-message nk-message-error small d-block mt-1">
                              {errors.projectType.message}
                            </span>
                          )}
                        </Form.Group>
                      </Col>
                      <Col xs={12}>
                        <Form.Group>
                          <Form.Label className="form-label">{t('contact.form.message')}</Form.Label>
                          <textarea
                            id="message"
                            className="form-control"
                            rows={5}
                            placeholder={t('contact.form.messagePh')}
                            {...register('message', { required: t('contact.validation.message') })}
                          />
                          {errors.message && (
                            <span className="nk-message nk-message-error small d-block mt-1">
                              {errors.message.message}
                            </span>
                          )}
                        </Form.Group>
                      </Col>
                      <Col xs={12}>
                        <NioButton
                          type="submit"
                          className="btn-primary btn-lg w-100"
                          label={sending ? t('contact.form.sending') : t('contact.form.submit')}
                          disabled={sending}
                        />
                      </Col>
                    </Row>
                  </form>
                </NioCard.Body>
              </NioCard>
            </Col>

            <Col lg={5}>
              <ContactInfoBlock settings={settings} />
            </Col>
          </Row>
        </Container>
      </NioSection>

      <NioSection className="pb-5 pb-lg-7">
        <Container>
          <Row className="g-4 g-lg-5">
            <Col lg={6}>
              <h2 className="h4 mb-3">{t('contact.why.h2')}</h2>
              <ul className="fs-16 text-gray-700 ps-3 mb-0">
                <li className="mb-2">{t('contact.why.li1')}</li>
                <li className="mb-2">{t('contact.why.li2')}</li>
                <li>{t('contact.why.li3')}</li>
              </ul>
            </Col>
            <Col lg={6}>
              <h2 className="h4 mb-3">{t('contact.map.h2')}</h2>
              <div className="rounded-3 overflow-hidden border border-gray-200 shadow-sm">
                <iframe
                  title={t('contact.map.iframeTitle')}
                  src={settings.mapEmbedUrl || 'https://maps.google.com/maps?q=2B+Rue+Alfred+Nobel,+77420+Champs-sur-Marne,+France&hl=fr&z=16&output=embed'}
                  width="100%"
                  height="320"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </Col>
          </Row>
        </Container>
      </NioSection>
    </AppLayout>
  );
}

export default ContactPage;
